import React, { useState } from "react";
import { useNavigate } from "react-router";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Badge } from "../components/duvi/Badge";
import { Input } from "../components/duvi/Input";
import {
  UserPlus,
  Users as UsersIcon,
  Shield,
  MoreVertical,
  X,
  Check,
} from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { UserProfileImage } from "../components/duvi/UserProfileImage";
import { users, me } from "../api/client";
import { useQuery, useMutation } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

function getInitials(name?: string): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function mapStatus(status: string): string {
  switch (status) {
    case "CONFIRMED":
      return "active";
    case "invited":
      return "invited";
    case "password_reset":
      return "password_reset";
    case "disabled":
    case "DISABLED":
      return "disabled";
    case "archived":
      return "archived";
    default:
      return status.toLowerCase();
  }
}

export function UserManagement() {
  const navigate = useNavigate();
  const [showInvitePanel, setShowInvitePanel] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");

  const { data: usersData, isLoading, refetch } = useQuery(() => users.list());

  // Fetch the current user's profile to identify self (isSelf guard on actions).
  // JS no longer has access to the JWT claims, so we ask the API instead.
  const { data: myProfile } = useQuery(() => me.get());
  const currentUserId = myProfile?.user_id ?? "";

  const inviteMutation = useMutation((data: { email: string; note?: string }) =>
    users.invite(data),
  );
  const disableMutation = useMutation((id: string) => users.disable(id));
  const enableMutation = useMutation((id: string) => users.enable(id));
  const unarchiveMutation = useMutation((id: string) => users.unarchive(id));
  const archiveMutation = useMutation((id: string) => users.archive(id));
  const forceResetMutation = useMutation((id: string) =>
    users.forceResetPassword(id),
  );

  const mappedUsers = (usersData ?? []).map((u) => ({
    id: u.user_id,
    name: u.name,
    email: u.email,
    avatarUrl: u.avatar_url,
    status: mapStatus(u.status),
    mfaEnabled: u.mfa_enabled ?? false,
    isSelf: u.user_id === currentUserId,
  }));

  const filteredUsers = showArchived
    ? mappedUsers
    : mappedUsers.filter((u) => u.status !== "archived");

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "invited":
        return "warning";
      case "password_reset":
        return "warning";
      case "disabled":
      case "archived":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "Active";
      case "invited":
        return "Invited";
      case "password_reset":
        return "Password Reset";
      case "disabled":
        return "Disabled";
      case "archived":
        return "Archived";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const handleSendInvite = async () => {
    try {
      await inviteMutation.mutate({
        email: inviteEmail,
        note: inviteNote || undefined,
      });
      setSentEmail(inviteEmail);
      setInviteSent(true);
      refetch();
    } catch {
      // Error is captured in inviteMutation.error
    }
  };

  const handleInviteAnother = () => {
    setInviteEmail("");
    setInviteNote("");
    setInviteSent(false);
  };

  const handleDone = () => {
    setShowInvitePanel(false);
    setInviteEmail("");
    setInviteNote("");
    setInviteSent(false);
  };

  const handleDisable = async (id: string) => {
    try {
      await disableMutation.mutate(id);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  const handleEnable = async (id: string) => {
    try {
      await enableMutation.mutate(id);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveMutation.mutate(id);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await unarchiveMutation.mutate(id);
      refetch();
    } catch {
      // Error captured in mutation
    }
  };

  const handleForceReset = async (id: string) => {
    try {
      await forceResetMutation.mutate(id);
    } catch {
      // Error captured in mutation
    }
  };

  const EmptyState = () => (
    <div
      data-testid={testIds.userEmpty}
      className="flex flex-col items-center justify-center py-16"
    >
      <UsersIcon
        className="w-16 h-16 text-duvi-text-secondary mb-6"
        strokeWidth={1}
      />
      <h2
        className="text-duvi-text-primary mb-2"
        style={{
          fontSize: "var(--text-lg)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        No platform admin users yet.
      </h2>
      <Button
        variant="primary"
        onClick={() => setShowInvitePanel(true)}
        className="mt-4"
      >
        <UserPlus className="w-4 h-4 mr-2" />
        Invite the first admin
      </Button>
    </div>
  );

  return (
    <AppShell breadcrumbs={["Platform", "Users"]} activeMenuItem="Users">
      <div data-testid={testIds.userManagementPage} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1
            className="text-duvi-text-primary"
            style={{
              fontSize: "var(--text-xl)",
              fontWeight: "var(--font-weight-semibold)",
              lineHeight: "var(--line-height-heading)",
            }}
          >
            Users
          </h1>
          <Button
            data-testid={testIds.userInviteButton}
            variant="primary"
            onClick={() => setShowInvitePanel(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite user
          </Button>
        </div>

        {/* Table */}
        {isLoading ? (
          <Card className="p-6">
            <div className="flex items-center justify-center py-16">
              <div
                className="text-duvi-text-secondary"
                style={{ fontSize: "var(--text-sm)" }}
              >
                Loading users...
              </div>
            </div>
          </Card>
        ) : filteredUsers.length === 0 && !showArchived ? (
          <Card className="p-6">
            <EmptyState />
          </Card>
        ) : (
          <Card className="overflow-hidden">
            {/* Show Archived Toggle */}
            <div className="flex items-center justify-end gap-2 px-4 pt-3">
              <Switch
                id="show-archived"
                data-testid={testIds.userShowArchived}
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label
                htmlFor="show-archived"
                className="text-duvi-text-secondary cursor-pointer"
                style={{ fontSize: "var(--text-xs)" }}
              >
                Show archived
              </Label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-duvi-border">
                  <tr>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      User
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Status
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      MFA
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Last Login
                    </th>
                    <th
                      className="text-left py-3 px-4 text-duvi-text-secondary"
                      style={{
                        fontSize: "var(--text-xs)",
                        fontWeight: "var(--font-weight-medium)",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      data-testid={testIds.userRow}
                      className={`border-b border-duvi-border hover:bg-duvi-elevated transition-colors ${
                        user.status === "disabled" || user.status === "archived"
                          ? "opacity-60"
                          : ""
                      }`}
                    >
                      {/* User */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <UserProfileImage
                            userId={user.id}
                            name={user.name}
                            size={32}
                          />
                          <div>
                            <div
                              className="text-duvi-text-primary"
                              style={{
                                fontSize: "var(--text-sm)",
                                fontWeight: "var(--font-weight-bold)",
                              }}
                            >
                              {user.name || user.email}
                            </div>
                            <div
                              className="text-duvi-text-secondary"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              {user.email}
                            </div>
                          </div>
                          {user.isSelf && (
                            <span
                              className="px-2 py-0.5 rounded-full bg-duvi-accent/10 text-duvi-accent"
                              style={{ fontSize: "var(--text-2xs)" }}
                            >
                              You
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4">
                        <Badge variant={getStatusBadgeVariant(user.status)}>
                          {getStatusLabel(user.status)}
                        </Badge>
                      </td>

                      {/* MFA */}
                      <td className="py-3 px-4">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block">
                              <Shield
                                className={`w-5 h-5 ${user.mfaEnabled ? "text-duvi-success" : "text-duvi-warning"}`}
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {user.mfaEnabled ? "TOTP enabled" : "Not set up"}
                          </TooltipContent>
                        </Tooltip>
                      </td>

                      {/* Last Login */}
                      <td className="py-3 px-4">
                        <span
                          className="text-duvi-text-muted"
                          style={{ fontSize: "var(--text-xs)" }}
                        >
                          —
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={testIds.userRowActions}
                              className="p-1 hover:bg-duvi-bg rounded transition-colors text-duvi-text-secondary"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-48 bg-duvi-surface border-duvi-border"
                          >
                            {user.status === "archived" ? (
                              <>
                                <DropdownMenuItem
                                  data-testid={testIds.userActionUnarchive}
                                  onClick={() => handleUnarchive(user.id)}
                                  className="text-duvi-text-primary hover:bg-duvi-elevated focus:bg-duvi-elevated cursor-pointer"
                                >
                                  Unarchive
                                </DropdownMenuItem>
                              </>
                            ) : (
                              <>
                                <DropdownMenuItem
                                  data-testid={testIds.userActionForceReset}
                                  disabled={
                                    user.isSelf || user.status === "invited"
                                  }
                                  onClick={() =>
                                    !user.isSelf &&
                                    user.status !== "invited" &&
                                    handleForceReset(user.id)
                                  }
                                  className={`hover:bg-duvi-elevated focus:bg-duvi-elevated ${user.isSelf || user.status === "invited" ? "text-duvi-text-muted cursor-not-allowed" : "text-duvi-text-primary cursor-pointer"}`}
                                >
                                  Force reset password
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  data-testid={
                                    user.status === "disabled"
                                      ? testIds.userActionEnable
                                      : testIds.userActionDisable
                                  }
                                  disabled={user.isSelf}
                                  onClick={() =>
                                    !user.isSelf &&
                                    (user.status === "disabled"
                                      ? handleEnable(user.id)
                                      : handleDisable(user.id))
                                  }
                                  className={`hover:bg-duvi-elevated focus:bg-duvi-elevated ${user.isSelf ? "text-duvi-text-muted cursor-not-allowed" : "text-duvi-text-primary cursor-pointer"}`}
                                >
                                  {user.status === "disabled"
                                    ? "Enable"
                                    : "Disable"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-duvi-border" />
                                <DropdownMenuItem
                                  data-testid={testIds.userActionArchive}
                                  disabled={user.isSelf}
                                  onClick={() =>
                                    !user.isSelf && handleArchive(user.id)
                                  }
                                  className={`hover:bg-duvi-elevated focus:bg-duvi-elevated ${user.isSelf ? "text-duvi-text-muted cursor-not-allowed" : "text-duvi-text-primary cursor-pointer"}`}
                                >
                                  Archive
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Invite User Slide-over Panel */}
      <>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
            showInvitePanel ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={handleDone}
        />

        {/* Panel */}
        <div
          className={`fixed top-0 right-0 h-full bg-duvi-elevated border-l border-duvi-border shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${
            showInvitePanel ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ width: "480px" }}
        >
          {!inviteSent ? (
            <>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-duvi-border">
                <h2
                  className="text-duvi-text-primary"
                  style={{
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-weight-bold)",
                  }}
                >
                  Invite platform admin
                </h2>
                <button
                  onClick={handleDone}
                  className="p-1 hover:bg-duvi-surface rounded transition-colors text-duvi-text-secondary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <div className="flex-1 overflow-y-auto px-6 py-6">
                <div className="space-y-4">
                  {/* Email Field */}
                  <div>
                    <label
                      className="block text-duvi-text-primary mb-2"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      Email address
                    </label>
                    <Input
                      data-testid={testIds.inviteUserEmail}
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="rob@example.com"
                    />
                  </div>

                  {/* Note Field */}
                  <div>
                    <label
                      className="block text-duvi-text-primary mb-2"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      Add a note to the invite email (optional)
                    </label>
                    <textarea
                      value={inviteNote}
                      onChange={(e) => setInviteNote(e.target.value)}
                      placeholder="This note will be included in the invitation email..."
                      className="w-full px-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary placeholder:text-duvi-text-muted focus:outline-none focus:border-duvi-accent transition-colors resize-none"
                      style={{
                        fontSize: "var(--text-sm)",
                        minHeight: "100px",
                      }}
                    />
                  </div>

                  {/* Error display */}
                  {inviteMutation.error && (
                    <div
                      className="text-duvi-destructive"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      {inviteMutation.error.message}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-5 border-t border-duvi-border space-y-3">
                <Button
                  data-testid={testIds.inviteUserSubmit}
                  variant="primary"
                  className="w-full"
                  onClick={handleSendInvite}
                  disabled={!inviteEmail || inviteMutation.isLoading}
                >
                  {inviteMutation.isLoading ? "Sending..." : "Send invite"}
                </Button>
                <div className="text-center">
                  <button
                    onClick={handleDone}
                    className="text-duvi-text-secondary hover:text-duvi-text-primary transition-colors"
                    style={{ fontSize: "var(--text-xs)" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                <div className="w-10 h-10 rounded-full bg-duvi-success/10 flex items-center justify-center mb-6">
                  <Check className="w-6 h-6 text-duvi-success" />
                </div>
                <h2
                  className="text-duvi-text-primary mb-3 text-center"
                  style={{
                    fontSize: "var(--text-lg)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  Invite sent to {sentEmail}
                </h2>
                <p
                  className="text-duvi-text-secondary text-center mb-8 max-w-sm"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  They'll receive an email with a link to set up their account.
                </p>
                <div className="flex flex-col gap-3 w-full max-w-xs">
                  <button
                    onClick={handleInviteAnother}
                    className="text-duvi-accent hover:underline"
                    style={{ fontSize: "var(--text-sm)" }}
                  >
                    Invite another
                  </button>
                  <Button
                    variant="secondary"
                    onClick={handleDone}
                    className="w-full"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </>
    </AppShell>
  );
}
